import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
// import { useHistory } from "react-router-dom";
import {
  clearUpdateUserSuccess,
  getLogUserRequest,
  updateUserRequest,
  logout,
} from "../../../redux/action";
// import { BottomNav } from "../../../components/Nav/BottomNav";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

// --- Reusable Input Component matching the new design ---
const InputField = ({
  label,
  name,
  type = "text",
  formik,
  iconSvg,
  placeholder,
}) => {
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full">
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2 ml-1">
        {label}
      </label>
      <div
        className={`relative flex items-center border rounded-lg bg-[#f8faff] transition-all overflow-hidden ${
          hasError
            ? "border-red-300 focus-within:border-red-500 focus-within:bg-white"
            : "border-[#dce5fc] focus-within:border-[#1a4bdf] focus-within:bg-white shadow-[0_2px_10px_rgba(26,75,223,0.02)]"
        }`}
      >
        {/* Left Icon */}
        {iconSvg && (
          <div
            className={`pl-4 pr-3 flex items-center justify-center ${hasError ? "text-red-400" : "text-slate-400"}`}
          >
            {iconSvg}
          </div>
        )}

        {/* Input Field - 16px to prevent iOS Safari Zoom */}
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className={`flex-1 py-2 bg-transparent text-[16px] text-slate-800 placeholder-slate-400 outline-none w-full ${!iconSvg ? "pl-4" : ""}`}
        />
      </div>
      {hasError && (
        <div className="mt-1.5 text-[12px] text-red-500 font-medium px-1">
          {formik.errors[name]}
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { updateUserSuccess, updateUserError, updateUserLoading, user, token } =
    useSelector((state) => ({
      updateUserSuccess: state?.ajaxStatuses?.success?.updateUser,
      updateUserError: state?.ajaxStatuses?.errors?.updateUser,
      updateUserLoading: state?.loadingIndicator?.updateUserLoading,
      user: state?.userData?.user,
      token: state?.userData?.token,
    }));

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      walletAddress: "",
      bankName: "",
      accountNumber: "",
      accountName: "",
      airtelMoneyAccount: "",
      mtnMoneyAccount: "",
      wac: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      // Add other validations as needed
    }),
    onSubmit: (values) => {
      dispatch(updateUserRequest({ formData: values, id: user?._id }));
    },
  });

  // --- Effects ---
  useEffect(() => {
    if (updateUserError) toast.error(updateUserError);
  }, [updateUserError]);

  useEffect(() => {
    if (updateUserSuccess) {
      toast.success("Profile updated! Logging out for security...");
      formik.resetForm();
      dispatch(clearUpdateUserSuccess());
      setTimeout(() => dispatch(logout()), 2000);
    }
  }, [updateUserSuccess, dispatch, formik]);

  useEffect(() => {
    if (token) dispatch(getLogUserRequest(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (user) {
      formik.setValues({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        email: user.email || "", // Added to match screenshot
        walletAddress: user.walletAddress || "",
        bankName: user.bankName || "",
        accountNumber: user.accountNumber || "",
        accountName: user.accountName || "",
        airtelMoneyAccount: user.airtelMoneyAccount || "",
        mtnMoneyAccount: user.mtnMoneyAccount || "",
        wac: user.wac || "",
      });
    }
  }, [user]);

  // --- Icon SVGs ---
  const userIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );
  const phoneIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );
  const mailIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22,6 12,13 2,6"></polyline>
    </svg>
  );
  const mobileIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
      <line x1="12" y1="18" x2="12.01" y2="18"></line>
    </svg>
  );
  const bankIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 21h18"></path>
      <path d="M3 10h18"></path>
      <path d="m5 6 7-3 7 3"></path>
      <line x1="4" y1="10" x2="4" y2="21"></line>
      <line x1="20" y1="10" x2="20" y2="21"></line>
      <line x1="8" y1="14" x2="8" y2="17"></line>
      <line x1="12" y1="14" x2="12" y2="17"></line>
      <line x1="16" y1="14" x2="16" y2="17"></line>
    </svg>
  );
  const walletIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
    </svg>
  );
  const hashIcon = (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="9" x2="20" y2="9"></line>
      <line x1="4" y1="15" x2="20" y2="15"></line>
      <line x1="10" y1="3" x2="8" y2="21"></line>
      <line x1="16" y1="3" x2="14" y2="21"></line>
    </svg>
  );

  return (
    /* Locked to w-[428px] to replicate the mobile app view exactly. */
    <div className="w-[428px] mx-auto bg-[#f4f7ff] min-h-screen relative font-sans text-slate-900 pb-[140px] shadow-[0_0_50px_rgba(0,0,0,0.1)] overflow-x-hidden overflow-y-auto">
      {/* --- Top Blue Header --- */}
      <div
        className="absolute top-0 left-0 w-full h-[200px] z-0 overflow-hidden rounded-b-3xl bg-no-repeat bg-right-top"
        style={{
          backgroundImage: "url('/img/new/background/africa-map.jpeg')",
          backgroundSize: "contain",
        }}
      >
        {/* Back Button */}
        <div className="relative z-10 px-6 pt-12 pb-2">
          <button
            onClick={history.goBack}
            className="flex items-center gap-2 text-white font-medium text-[15px] active:scale-95 transition-transform"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back
          </button>
        </div>

        {/* Title Area */}
        <div className="relative z-10 px-6 pt-2">
          <h1 className="text-[32px] font-semibold text-white mb-1">
            My Profile
          </h1>
          <p className="text-[13px] text-blue-100 font-medium">
            Manage your personal and financial details
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-[160px] px-4 flex flex-col gap-4">
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          {/* --- Section 1: Personal Details --- */}
          <div className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-50">
            {/* Card Header */}
            <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
              <div className="w-10 h-10 bg-[#1a4bdf] rounded-[0.8rem] flex items-center justify-center text-white shadow-md shadow-blue-600/20">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">
                  Personal Details
                </h3>
                <p className="text-[11px] text-slate-500 font-medium">
                  Your personal information
                </p>
              </div>
            </div>

            {/* Fields */}
            <div className="flex flex-col gap-5">
              <div className="flex gap-4">
                <InputField
                  label="First Name"
                  name="firstName"
                  placeholder="e.g. Sam"
                  iconSvg={userIcon}
                  formik={formik}
                />
                <InputField
                  label="Last Name"
                  name="lastName"
                  placeholder="e.g. James"
                  iconSvg={userIcon}
                  formik={formik}
                />
              </div>
              <InputField
                label="Phone Number"
                name="phone"
                placeholder="123456789"
                iconSvg={phoneIcon}
                formik={formik}
              />
            </div>
          </div>

          {/* --- Section 2: Mobile Money --- */}
          <div className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-50">
            <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
              <div className="w-10 h-10 bg-[#1a4bdf] rounded-[0.8rem] flex items-center justify-center text-white shadow-md shadow-blue-600/20">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">
                  Mobile Money
                </h3>
                <p className="text-[11px] text-slate-500 font-medium">
                  Your mobile money accounts
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <InputField
                label="Airtel Money Account"
                name="airtelMoneyAccount"
                placeholder="0959946"
                iconSvg={mobileIcon}
                formik={formik}
              />
              <InputField
                label="MTN Money Account"
                name="mtnMoneyAccount"
                placeholder="e.g. +256 777 000 000"
                iconSvg={mobileIcon}
                formik={formik}
              />
              <InputField
                label="WAC"
                name="wac"
                placeholder="NX9T3H7R"
                iconSvg={mobileIcon}
                formik={formik}
              />
            </div>
          </div>

          {/* --- Section 3: Financial Settings --- */}
          <div className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-50 mb-6">
            <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
              <div className="w-10 h-10 bg-[#1a4bdf] rounded-[0.8rem] flex items-center justify-center text-white shadow-md shadow-blue-600/20">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M11.5 2L2 7v2h19V7l-9.5-5zM3 10v7h2v-7H3zm6 0v7h2v-7H9zm6 0v7h2v-7h-2zm-9 9v2h11v-2H6z" />
                </svg>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">
                  Financial Settings
                </h3>
                <p className="text-[11px] text-slate-500 font-medium">
                  Your financial and bank information
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex gap-4">
                <InputField
                  label="Wallet Address"
                  name="walletAddress"
                  placeholder="0x..."
                  iconSvg={walletIcon}
                  formik={formik}
                />
                <InputField
                  label="Bank Name"
                  name="bankName"
                  placeholder="Bank Name"
                  iconSvg={bankIcon}
                  formik={formik}
                />
              </div>
              <InputField
                label="Account Number"
                name="accountNumber"
                placeholder="# 0959946637"
                iconSvg={hashIcon}
                formik={formik}
              />
              <InputField
                label="Account Name"
                name="accountName"
                placeholder="Sam James"
                iconSvg={userIcon}
                formik={formik}
              />
            </div>
          </div>

          {/* --- Save Changes Button (Sticky Bottom) --- */}
          <div className="fixed bottom-[20px] w-[428px] px-4 left-1/2 -translate-x-1/2 z-40">
            <button
              type="button"
              onClick={formik.handleSubmit}
              disabled={updateUserLoading}
              className="w-full bg-[#1a4bdf] hover:bg-blue-700 disabled:bg-[#a5bcf6] disabled:cursor-not-allowed text-white rounded-xl py-4 flex items-center justify-center gap-2 relative shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
            >
              {updateUserLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <polyline points="9 12 11 14 15 10"></polyline>
                </svg>
              )}
              <span className="text-[16px] font-semibold">
                {updateUserLoading ? "Saving..." : "Save Changes"}
              </span>
            </button>
          </div>
        </form>
      </div>

      {/* Include your bottom navigation component here if required */}
      {/* <div className="fixed bottom-0 w-[428px] z-40 bg-white">
        <BottomNav />
      </div> */}
    </div>
  );
};

export default Profile;
